import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "../../components/Common/withRouter";
import SimpleBar from "simplebar-react";
import Api from "../../api/Api";

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.login.token);
  const user = useSelector((state) => state.login.user);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [modal, setModal] = useState(false);
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // For dashboard users, allow all authenticated users to create notifications
  const isAdmin = true; // Dashboard users can create notifications

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    user_id: "",
    user_type: "",
    scheduled_at: "",
    data: {},
  });

  const [broadcastData, setBroadcastData] = useState({
    title: "",
    message: "",
    type: "info",
    user_type: "user",
    scheduled_at: "",
    data: {},
  });

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("type", filterType);
      if (showUnreadOnly) params.append("unread_only", "true");

      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;

      const response = await Api.get(`/notifications?${params.toString()}`);
      if (response.data.success) {
        setNotifications(response.data.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error.response);
      toast.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;

      const response = await Api.get("/notifications/unread/count");
      if (response.data.success) {
        setUnreadCount(response.data.data.unread_count);
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [filterType, showUnreadOnly]);

  // Mark notification as read
  const markAsRead = async (id) => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;

      const response = await Api.put(`/notifications/${id}/read`);
      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === id
              ? { ...notif, is_read: true, read_at: new Date().toISOString() }
              : notif
          )
        );
        fetchUnreadCount();
        toast.success("Notification marked as read");
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;

      const response = await Api.put("/notifications/mark-all-read");
      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((notif) => ({
            ...notif,
            is_read: true,
            read_at: new Date().toISOString(),
          }))
        );
        setUnreadCount(0);
        toast.success("All notifications marked as read");
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Failed to mark all notifications as read");
    }
  };

  // Delete notification
  const deleteNotification = async (id) => {
    if (window.confirm("Are you sure you want to delete this notification?")) {
      try {
        // Set authorization header
        Api.defaults.headers.common["Authorization"] = "Bearer " + token;

        const response = await Api.delete(`/notifications/${id}`);
        if (response.data.success) {
          setNotifications((prev) => prev.filter((notif) => notif.id !== id));
          fetchUnreadCount();
          toast.success("Notification deleted successfully");
        }
      } catch (error) {
        console.error("Error deleting notification:", error);
        toast.error("Failed to delete notification");
      }
    }
  };

  // Create notification
  const createNotification = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;

      const response = await Api.post("admin/notifications", formData);
      if (response.data.success) {
        toast.success("Notification created successfully");
        setModal(false);
        setFormData({
          title: "",
          message: "",
          type: "info",
          user_id: "",
          user_type: "",
          scheduled_at: "",
          data: {},
        });
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error creating notification:", error.response);
      toast.error("Failed to create notification");
    }
  };

  // Send broadcast notification
  const sendBroadcast = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;

      const response = await Api.post(
        "/notifications/broadcast",
        broadcastData
      );
      if (response.data.success) {
        toast.success("Broadcast notification sent successfully");
        setBroadcastModal(false);
        setBroadcastData({
          title: "",
          message: "",
          type: "info",
          user_type: "user",
          scheduled_at: "",
          data: {},
        });
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error sending broadcast:", error);
      toast.error("Failed to send broadcast notification");
    }
  };

  // Get notification type badge color
  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "success":
        return "success";
      case "warning":
        return "warning";
      case "error":
        return "danger";
      default:
        return "info";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <CardTitle className="mb-0">
                      Notifications Management
                    </CardTitle>
                    <div className="d-flex gap-2">
                      <Badge color="primary" className="p-2">
                        Unread: {unreadCount}
                      </Badge>
                      <Dropdown
                        isOpen={dropdownOpen}
                        toggle={() => setDropdownOpen(!dropdownOpen)}
                      >
                        <DropdownToggle caret color="primary">
                          Filter
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem
                            active={filterType === "all"}
                            onClick={() => setFilterType("all")}
                          >
                            All Types
                          </DropdownItem>
                          <DropdownItem
                            active={filterType === "info"}
                            onClick={() => setFilterType("info")}
                          >
                            Info
                          </DropdownItem>
                          <DropdownItem
                            active={filterType === "success"}
                            onClick={() => setFilterType("success")}
                          >
                            Success
                          </DropdownItem>
                          <DropdownItem
                            active={filterType === "warning"}
                            onClick={() => setFilterType("warning")}
                          >
                            Warning
                          </DropdownItem>
                          <DropdownItem
                            active={filterType === "error"}
                            onClick={() => setFilterType("error")}
                          >
                            Error
                          </DropdownItem>
                          <DropdownItem divider />
                          <DropdownItem
                            active={showUnreadOnly}
                            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                          >
                            {showUnreadOnly ? "Show All" : "Unread Only"}
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                      {isAdmin && (
                        <>
                          <Button
                            color="success"
                            onClick={() => setBroadcastModal(true)}
                          >
                            <i className="mdi mdi-broadcast me-1"></i>
                            Broadcast
                          </Button>
                          <Button
                            color="primary"
                            onClick={() => setModal(true)}
                          >
                            <i className="mdi mdi-plus me-1"></i>
                            Create
                          </Button>
                        </>
                      )}
                      <Button color="secondary" onClick={markAllAsRead}>
                        <i className="mdi mdi-check-all me-1"></i>
                        Mark All Read
                      </Button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2">Loading notifications...</p>
                    </div>
                  ) : (
                    <SimpleBar style={{ maxHeight: "600px" }}>
                      {notifications.length === 0 ? (
                        <Alert color="info" className="text-center">
                          No notifications found
                        </Alert>
                      ) : (
                        notifications.map((notification) => (
                          <Card
                            key={notification.id}
                            className={`mb-3 ${
                              !notification.is_read ? "border-primary" : ""
                            }`}
                          >
                            <CardBody>
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <h6 className="mb-0 me-2">
                                      {notification.title}
                                    </h6>
                                    <Badge
                                      color={getTypeBadgeColor(
                                        notification.type
                                      )}
                                    >
                                      {notification.type}
                                    </Badge>
                                    {!notification.is_read && (
                                      <Badge color="primary" className="ms-2">
                                        New
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-muted mb-2">
                                    {notification.message}
                                  </p>
                                  <div className="d-flex align-items-center text-muted small">
                                    <span className="me-3">
                                      <i className="mdi mdi-clock me-1"></i>
                                      {formatDate(notification.created_at)}
                                    </span>
                                    {notification.user_id && (
                                      <span className="me-3">
                                        <i className="mdi mdi-account me-1"></i>
                                        User ID: {notification.user_id}
                                      </span>
                                    )}
                                    {notification.user_type && (
                                      <span className="me-3">
                                        <i className="mdi mdi-account-group me-1"></i>
                                        {notification.user_type}
                                      </span>
                                    )}
                                    {notification.scheduled_at && (
                                      <span className="me-3">
                                        <i className="mdi mdi-calendar-clock me-1"></i>
                                        Scheduled:{" "}
                                        {formatDate(notification.scheduled_at)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex gap-1">
                                  {!notification.is_read && (
                                    <Button
                                      size="sm"
                                      color="primary"
                                      onClick={() =>
                                        markAsRead(notification.id)
                                      }
                                    >
                                      <i className="mdi mdi-check"></i>
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    color="danger"
                                    onClick={() =>
                                      deleteNotification(notification.id)
                                    }
                                  >
                                    <i className="mdi mdi-delete"></i>
                                  </Button>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        ))
                      )}
                    </SimpleBar>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Create Notification Modal - Admin Only */}
      {isAdmin && (
        <Modal isOpen={modal} toggle={() => setModal(!modal)} size="lg">
          <ModalHeader toggle={() => setModal(!modal)}>
            Create New Notification
          </ModalHeader>
          <ModalBody>
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Title</Label>
                    <Input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="Enter notification title"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="error">Error</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Message</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder="Enter notification message"
                />
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>User ID (Optional)</Label>
                    <Input
                      type="number"
                      value={formData.user_id}
                      onChange={(e) =>
                        setFormData({ ...formData, user_id: e.target.value })
                      }
                      placeholder="Specific user ID"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>User Type (Optional)</Label>
                    <Input
                      type="select"
                      value={formData.user_type}
                      onChange={(e) =>
                        setFormData({ ...formData, user_type: e.target.value })
                      }
                    >
                      <option value="">Select user type</option>
                      <option value="user">User</option>
                      <option value="business">Business</option>
                      <option value="influencer">Influencer</option>
                      <option value="admin">Admin</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Scheduled At (Optional)</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) =>
                    setFormData({ ...formData, scheduled_at: e.target.value })
                  }
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModal(!modal)}>
              Cancel
            </Button>
            <Button color="primary" onClick={createNotification}>
              Create Notification
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Broadcast Notification Modal - Admin Only */}
      {isAdmin && (
        <Modal
          isOpen={broadcastModal}
          toggle={() => setBroadcastModal(!broadcastModal)}
          size="lg"
        >
          <ModalHeader toggle={() => setBroadcastModal(!broadcastModal)}>
            Send Broadcast Notification
          </ModalHeader>
          <ModalBody>
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Title</Label>
                    <Input
                      type="text"
                      value={broadcastData.title}
                      onChange={(e) =>
                        setBroadcastData({
                          ...broadcastData,
                          title: e.target.value,
                        })
                      }
                      placeholder="Enter notification title"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      value={broadcastData.type}
                      onChange={(e) =>
                        setBroadcastData({
                          ...broadcastData,
                          type: e.target.value,
                        })
                      }
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="error">Error</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Message</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={broadcastData.message}
                  onChange={(e) =>
                    setBroadcastData({
                      ...broadcastData,
                      message: e.target.value,
                    })
                  }
                  placeholder="Enter notification message"
                />
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Target User Type</Label>
                    <Input
                      type="select"
                      value={broadcastData.user_type}
                      onChange={(e) =>
                        setBroadcastData({
                          ...broadcastData,
                          user_type: e.target.value,
                        })
                      }
                    >
                      <option value="user">All Users</option>
                      <option value="business">All Businesses</option>
                      <option value="influencer">All Influencers</option>
                      <option value="admin">All Admins</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Scheduled At (Optional)</Label>
                    <Input
                      type="datetime-local"
                      value={broadcastData.scheduled_at}
                      onChange={(e) =>
                        setBroadcastData({
                          ...broadcastData,
                          scheduled_at: e.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setBroadcastModal(!broadcastModal)}
            >
              Cancel
            </Button>
            <Button color="success" onClick={sendBroadcast}>
              Send Broadcast
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default NotificationsPage;
